class SXylitolToxicityCalculator extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.abortController = null;
    this.weightUnit = "kg";

    this.products = {
      gum: {
        label: "How many pieces of gum?",
        help: "One piece of high-xylitol gum can hold a surprising amount. If unsure, round up.",
        units: [["piece", "pieces", 1]],
        gramsLow: 0.22,
        gramsHigh: 1
      },
      mints: {
        label: "How many mints / lollies?",
        help: "Estimate the number of pieces.",
        units: [["piece", "pieces", 1]],
        gramsLow: 0.03,
        gramsHigh: 0.06
      },
      pure: {
        label: "How much pure xylitol?",
        help: "Granulated baking xylitol is approximately 100% xylitol, so even a small amount matters.",
        units: [
          ["tsp", "teaspoons", 4],
          ["tbsp", "tablespoons", 12],
          ["cup", "cups", 190],
          ["g", "grams", 1]
        ],
        concentration: 1
      },
      peanut: {
        label: "How much peanut butter?",
        help: "Estimate as best you can and keep the packaging.",
        undisclosed: true,
        units: [
          ["tsp", "teaspoons", 1],
          ["tbsp", "tablespoons", 1],
          ["jar", "part of jar", 1]
        ]
      },
      baked: {
        label: "How much did they eat?",
        help: "Estimate as best you can and keep the packaging.",
        undisclosed: true,
        units: [
          ["bite", "bites", 1],
          ["item", "items", 1]
        ]
      },
      toothpaste: {
        label: "How much toothpaste?",
        help: "Estimate as best you can and keep the packaging.",
        undisclosed: true,
        units: [
          ["lick", "licks", 1],
          ["tube", "part of tube", 1]
        ]
      },
      exact: {
        label: "How much xylitol exactly?",
        help: "Enter the amount of xylitol itself from the label or product information.",
        exact: true
      }
    };
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.abortController = new AbortController();
    this.cacheElements();
    this.bindEvents();
    this.renderProductFields();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = null;
    this.initialized = false;
  }

  cacheElements() {
    this.form = this.querySelector("[data-xtc-form]");
    this.weightInput = this.querySelector("[data-xtc-weight]");
    this.productSelect = this.querySelector("[data-xtc-product]");
    this.amountInput = this.querySelector("[data-xtc-amount]");
    this.amountUnitSelect = this.querySelector("[data-xtc-amount-unit]");
    this.amountLabel = this.querySelector("[data-xtc-amount-label]");
    this.amountHelp = this.querySelector("[data-xtc-amount-help]");
    this.exactWrap = this.querySelector("[data-xtc-exact-wrap]");
    this.timeSelect = this.querySelector("[data-xtc-time]");
    this.errorElement = this.querySelector("[data-xtc-error]");
    this.result = this.querySelector("[data-xtc-result]");
    this.resultHead = this.querySelector("[data-xtc-result-head]");
    this.resultTag = this.querySelector("[data-xtc-result-tag]");
    this.resultTitle = this.querySelector("[data-xtc-result-title]");
    this.resultSubtitle = this.querySelector("[data-xtc-result-subtitle]");
    this.resultDose = this.querySelector("[data-xtc-result-dose]");
    this.doNowList = this.querySelector("[data-xtc-do-now]");
    this.watchList = this.querySelector("[data-xtc-watch]");
    this.work = this.querySelector("[data-xtc-work]");
    this.restartButton = this.querySelector("[data-xtc-restart]");
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.calculate();
    }, { signal });

    this.productSelect.addEventListener("change", () => {
      this.renderProductFields();
      this.hideResult();
    }, { signal });

    this.querySelectorAll("[data-xtc-weight-unit]").forEach((button) => {
      button.addEventListener("click", () => {
        this.setWeightUnit(button.dataset.xtcWeightUnit);
      }, { signal });
    });

    this.restartButton.addEventListener("click", () => {
      this.restart();
    }, { signal });
  }

  setWeightUnit(nextUnit) {
    if (nextUnit === this.weightUnit) {
      return;
    }

    const currentValue = Number.parseFloat(this.weightInput.value);

    if (Number.isFinite(currentValue) && currentValue > 0) {
      const converted = nextUnit === "lb"
        ? currentValue * 2.2046226218
        : currentValue / 2.2046226218;

      this.weightInput.value = this.formatInputNumber(converted);
    }

    this.weightUnit = nextUnit;

    this.querySelectorAll("[data-xtc-weight-unit]").forEach((button) => {
      const isActive = button.dataset.xtcWeightUnit === nextUnit;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
    });

    this.hideResult();
  }

  formatInputNumber(value) {
    return Number.parseFloat(value.toFixed(2)).toString();
  }

  renderProductFields() {
    const product = this.products[this.productSelect.value];

    if (!product) {
      return;
    }

    this.amountLabel.textContent = product.label;
    this.amountHelp.textContent = product.help || "Estimate as best you can. If unsure, round up.";
    this.amountUnitSelect.innerHTML = "";

    if (product.exact) {
      this.exactWrap.hidden = false;
      this.amountUnitSelect.hidden = true;
      return;
    }

    this.exactWrap.hidden = true;
    this.amountUnitSelect.hidden = false;

    product.units.forEach((unit, index) => {
      const option = document.createElement("option");
      option.value = String(index);
      option.textContent = unit[1];
      this.amountUnitSelect.appendChild(option);
    });
  }

  calculate() {
    this.clearError();

    const species = this.querySelector("[data-xtc-species]:checked")?.value || "dog";
    const weight = Number.parseFloat(this.weightInput.value);
    const amount = Number.parseFloat(this.amountInput.value);
    const productKey = this.productSelect.value;
    const product = this.products[productKey];
    const time = this.timeSelect.value;

    if (!Number.isFinite(weight) || weight <= 0) {
      this.showError("Please enter your pet's weight using a number greater than zero.");
      this.weightInput.focus();
      return;
    }

    if (weight > 250 && this.weightUnit === "kg") {
      this.showError("Please check the weight and selected unit. The entered weight is unusually high in kilograms.");
      this.weightInput.focus();
      return;
    }

    if (weight > 550 && this.weightUnit === "lb") {
      this.showError("Please check the weight and selected unit. The entered weight is unusually high in pounds.");
      this.weightInput.focus();
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      this.showError("Please enter how much was eaten using a number greater than zero.");
      this.amountInput.focus();
      return;
    }

    const weightKg = this.weightUnit === "lb" ? weight / 2.2046226218 : weight;

    if (species === "cat") {
      this.renderCat();
      return;
    }

    if (product.undisclosed) {
      this.renderUnknown(productKey);
      return;
    }

    let doseLow;
    let doseHigh;

    if (product.exact) {
      const exactUnit = this.querySelector("[data-xtc-exact-unit]:checked")?.value || "mg";
      const grams = exactUnit === "mg" ? amount / 1000 : amount;
      doseLow = (grams * 1000) / weightKg;
      doseHigh = doseLow;
    } else if (product.concentration) {
      const selectedUnit = product.units[Number(this.amountUnitSelect.value)];
      const grams = selectedUnit[2] * amount * product.concentration;
      doseLow = (grams * 1000) / weightKg;
      doseHigh = doseLow;
    } else {
      doseLow = (product.gramsLow * amount * 1000) / weightKg;
      doseHigh = (product.gramsHigh * amount * 1000) / weightKg;
    }

    if (![doseLow, doseHigh].every(Number.isFinite)) {
      this.showError("We couldn't calculate this exposure. Please check the entered values.");
      return;
    }

    this.renderDose(doseLow, doseHigh, time);
  }

  get hypoglycaemiaThreshold() {
    const value = Number.parseFloat(this.dataset.hypoThreshold);
    return Number.isFinite(value) && value > 0 ? value : 100;
  }

  get liverThreshold() {
    const value = Number.parseFloat(this.dataset.liverThreshold);
    return Number.isFinite(value) && value > 0 ? value : 500;
  }

  getFactorNote() {
    const selected = new Set(
      Array.from(this.querySelectorAll("[data-xtc-factor]:checked")).map((input) => input.dataset.xtcFactor)
    );
    const notes = [];

    if (selected.has("diabetic")) {
      notes.push("a diabetic dog's blood sugar may be harder to manage");
    }

    if (selected.has("liver")) {
      notes.push("an existing liver problem increases concern at higher exposures");
    }

    if (selected.has("small") || selected.has("puppy")) {
      notes.push("small dogs and puppies reach a high dose from very small amounts");
    }

    if (selected.has("pregnant")) {
      notes.push("pregnancy is an additional reason to be cautious");
    }

    if (selected.has("senior")) {
      notes.push("senior dogs may tolerate illness less well");
    }

    if (notes.length === 0) {
      return "";
    }

    return ` You also told us ${notes.join(", and ")}, so contact a veterinarian promptly.`;
  }

  renderDose(low, high, time) {
    const factorNote = this.getFactorNote();
    const isLiverRisk = high >= this.liverThreshold;
    const isHypoglycaemiaRisk = high >= this.hypoglycaemiaThreshold;
    const doseText = Math.abs(high - low) < 0.5
      ? `${this.formatDose(high)} mg/kg`
      : `${this.formatDose(low)}–${this.formatDose(high)} mg/kg`;

    if (isLiverRisk) {
      this.setResultHeader(
        "is-high",
        "Emergency",
        "Potential liver-injury dose. Treat this as an emergency.",
        `At the high end, the estimate reaches the configured liver-risk threshold as well as the blood-sugar threshold. Your dog needs veterinary advice now.${factorNote}`
      );
    } else if (isHypoglycaemiaRisk) {
      this.setResultHeader(
        "is-high",
        "Emergency",
        "Enough to cause dangerously low blood sugar.",
        `The high-end estimate reaches the configured hypoglycaemia threshold. This is an emergency even if your dog currently looks normal.${factorNote}`
      );
    } else {
      this.setResultHeader(
        "is-low",
        "Lower calculated risk",
        "Below the configured toxic-dose threshold — but stay alert.",
        `The estimate is below the configured threshold. Because xylitol content varies between brands and may be undisclosed, contact a veterinarian or poisons service to confirm.${factorNote}`
      );
    }

    this.resultDose.innerHTML = `Estimated dose: <b>${doseText}</b> &nbsp;|&nbsp; Hypoglycaemia threshold: <b>${this.hypoglycaemiaThreshold} mg/kg</b>; liver threshold: <b>${this.liverThreshold} mg/kg</b>.`;

    if (isHypoglycaemiaRisk) {
      this.renderList(this.doNowList, [
        "<b>Call your veterinarian or an emergency clinic now</b> and say that the exposure involves xylitol.",
        "Do <b>not</b> try to make your dog vomit unless a veterinary professional tells you to.",
        "Take the packaging and note the estimated amount and time of exposure.",
        "If your dog is weak, wobbly, trembling or collapsing, tell the clinic immediately while travelling."
      ]);
    } else {
      this.renderList(this.doNowList, [
        "<b>Call your veterinarian or an animal poisons service</b> and read them the estimated dose.",
        "Keep the packaging and note the time of exposure.",
        "Watch closely over the next 12 hours, following professional advice.",
        "Do not wait for symptoms if the product amount or xylitol concentration is uncertain."
      ]);
    }

    this.renderList(this.watchList, [
      "Vomiting, weakness or wobbliness",
      "Stumbling, tremors or seizures",
      "Sudden lethargy or collapse",
      "Later signs such as yellow gums, bruising or ongoing vomiting"
    ]);

    const timeText = time === "late"
      ? "More than 12 hours have passed, so a veterinarian may recommend examination or blood tests even if early signs were missed."
      : "Signs may begin quickly, and professional monitoring may be needed even before symptoms appear.";

    this.work.innerHTML = `We divided the estimated xylitol amount by your dog's weight to calculate approximately <b>${doseText}</b>. For products with a published range, classification uses the high end because individual brands vary. ${timeText}`;
    this.revealResult();
  }

  renderUnknown(productKey) {
    const productName = productKey === "peanut"
      ? "peanut butter"
      : productKey === "toothpaste"
        ? "this dental product"
        : "this product";

    this.setResultHeader(
      "is-unknown",
      "Assume dangerous",
      "The xylitol dose cannot be calculated safely from this product.",
      `The xylitol amount in ${productName} is often not shown and can vary greatly between brands. A numerical result would be misleading, so treat the exposure as potentially toxic.${this.getFactorNote()}`
    );

    this.resultDose.innerHTML = `Unknown concentration: even a small serving can be significant for a small dog. The configured blood-sugar threshold is <b>${this.hypoglycaemiaThreshold} mg/kg</b>.`;

    this.renderList(this.doNowList, [
      "<b>Call your veterinarian or an emergency clinic now</b> and identify the exact product and brand.",
      "Keep or photograph the packaging and ingredient list.",
      "Note the maximum amount that may have been eaten and when it happened.",
      "Do not make your dog vomit unless a veterinary professional tells you to."
    ]);

    this.renderList(this.watchList, [
      "Vomiting, weakness or wobbliness",
      "Tremors, stumbling or seizures",
      "Sudden collapse",
      "Later yellow gums, bruising or ongoing vomiting"
    ]);

    this.work.textContent = "This product does not provide enough information for a responsible dose calculation. The calculator therefore avoids inventing a concentration and returns a safety-first result.";
    this.revealResult();
  }

  renderCat() {
    this.setResultHeader(
      "is-low",
      "Different risk profile",
      "This calculator's dose thresholds are designed for dogs.",
      "Current veterinary references do not describe the same xylitol toxicity pattern in cats, but the product may contain other hazardous ingredients. Contact a veterinarian if your cat is unwell or the product also contains chocolate, caffeine, medication or another toxin."
    );

    this.resultDose.textContent = "No dog xylitol dose calculation has been applied to this cat exposure.";

    this.renderList(this.doNowList, [
      "Check the complete ingredient list for other hazards.",
      "Keep the packaging and monitor your cat closely.",
      "Contact a veterinarian if your cat has symptoms or ate a large amount of an unknown product."
    ]);

    this.renderList(this.watchList, [
      "Vomiting or loss of appetite",
      "Lethargy, weakness or hiding",
      "Any unusual behaviour over the next 24 hours"
    ]);

    this.work.textContent = "The numerical thresholds in this tool apply to dogs. For cats, the result focuses on other ingredients and clinical signs rather than presenting a misleading dog-based dose classification.";
    this.revealResult();
  }

  setResultHeader(className, tag, title, subtitle) {
    this.resultHead.className = `s-xylitol-toxicity-calculator__result-head ${className}`;
    this.resultTag.textContent = tag;
    this.resultTitle.textContent = title;
    this.resultSubtitle.textContent = subtitle;
  }

  renderList(element, items) {
    element.innerHTML = items.map((item) => `<li>${item}</li>`).join("");
  }

  formatDose(value) {
    if (value < 10) {
      return Number.parseFloat(value.toFixed(1)).toString();
    }

    return Math.round(value).toString();
  }

  showError(message) {
    this.errorElement.textContent = message;
    this.errorElement.classList.add("is-visible");
  }

  clearError() {
    this.errorElement.textContent = "";
    this.errorElement.classList.remove("is-visible");
  }

  revealResult() {
    this.result.hidden = false;
    this.result.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  hideResult() {
    this.result.hidden = true;
  }

  restart() {
    this.form.reset();
    this.weightUnit = "kg";
    this.querySelectorAll("[data-xtc-weight-unit]").forEach((button) => {
      const isKg = button.dataset.xtcWeightUnit === "kg";
      button.classList.toggle("is-active", isKg);
      button.setAttribute("aria-pressed", String(isKg));
    });
    this.renderProductFields();
    this.clearError();
    this.hideResult();
    this.form.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

if (!customElements.get("s-xylitol-toxicity-calculator")) {
  customElements.define("s-xylitol-toxicity-calculator", SXylitolToxicityCalculator);
}
